import React, { useEffect, useState } from "react"
import { Link, useStaticQuery, graphql } from "gatsby"
import { StaticImage, GatsbyImage, getImage } from "gatsby-plugin-image"
import * as styles from "./header.module.css"
import { useLocation } from "@reach/router"
import { NAV_LINKS } from "./nav-links"
import BodyText from "../text/body-text/body-text"
import Button from "../button/button"
import RegistrationForm from "../CustomDialogs/RegistrationForm"

const Header = ({ bgColor, headerContainerStyles, divRef }) => {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [addStyling, setAddStyling] = useState(false)
  const [isRegistrationFormOpen, setIsRegistrationFormOpen] = useState(false)

  const navigationLinks = [
    // { text: "Home", url: NAV_LINKS.HOME },
    { text: "Courses", url: NAV_LINKS.COURSES },
    { text: "Learnet Institute", url: NAV_LINKS.LEARNET_INSTITUTE },
    { text: "CSR-Sponsored Initiatives", url: NAV_LINKS.CORPORATE_CHANGEMAKERS },
    {
      text: "More",
      list: [
        { text: "About Us", url: NAV_LINKS.ABOUT },
        // { text: "Partners", url: NAV_LINKS.PARTNERS },
        { text: "Contact Us", url: NAV_LINKS.CONTACT_US },
      ],
    },
    {
      text: "Register",
      onClick: () => {
        setIsRegistrationFormOpen(true)
      },
    },
  ]

  const toggleDrawer = () => setDrawerOpen(!drawerOpen)
  const closeDrawer = () => setDrawerOpen(false)

  const location = useLocation()

  const isActiveLink = url =>
    location.pathname == url || location.pathname == url + "/"

  // Disable scrolling when drawer is open
  useEffect(() => {
    if (drawerOpen) {
      document.documentElement.style.overflow = "hidden"
    } else {
      document.documentElement.style.overflow = ""
    }
    return () => {
      document.documentElement.style.overflow = ""
    }
  }, [drawerOpen])

  useEffect(() => {
    if (!divRef) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAddStyling(false)
        } else {
          setAddStyling(true)
        }
      },
      { threshold: 0 }
    )

    // eslint-disable-next-line react/prop-types
    const currentDiv = divRef.current

    if (currentDiv) {
      observer.observe(currentDiv)
    }

    return () => {
      if (currentDiv) {
        observer.unobserve(currentDiv)
      }
    }
  }, [divRef])

  const renderMobileSection = () => {
    return (
      <>
        <div
          className="flex items-center px-3 py-2 border rounded text-[#2F57EF] border-[#2F57EF] cursor-pointer"
          onClick={toggleDrawer}
        >
          <HamburgerIcon />
        </div>

        {/* Drawer Overlay */}
        {drawerOpen && (
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-50 md:hidden"
            onClick={closeDrawer}
          ></div>
        )}

        {/* Drawer for mobile */}
        <div
          className={`md:hidden fixed top-0 right-0 h-full w-64 bg-white shadow-lg z-50 transition-transform duration-300 ease-in-out ${
            drawerOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="p-4">
            <div
              onClick={closeDrawer}
              className="absolute top-3 right-3 text-[#2F57EF] hover:text-[#2F57EF] no-underline"
            >
              <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
                <path d="M18.36 5.64a1 1 0 0 0-1.41 0L12 10.59 7.05 5.64a1 1 0 0 0-1.41 1.41L10.59 12l-5.05 5.05a1 1 0 0 0 1.41 1.41L12 13.41l5.05 5.05a1 1 0 0 0 1.41-1.41L13.41 12l5.05-5.05a1 1 0 0 0 0-1.41z" />
              </svg>
            </div>
            <div className="mt-3 flex flex-col space-y-4">
              {navigationLinks.map((link, index) =>
                link.url ? (
                  <Link
                    key={index}
                    to={link.url}
                    className={`${
                      isActiveLink(link.url)
                        ? "underline underline-offset-8 decoration-4 decoration-[#2F57EF] text-[#000000] font-semibold"
                        : "text-[#626262] no-underline"
                    } hover:text-[#2F57EF]`}
                    onClick={closeDrawer}
                  >
                    {link.text}
                  </Link>
                ) : link.list ? (
                  <React.Fragment key={index}>
                    {link.list.map((val, hIndex) => (
                      <Link
                        key={hIndex}
                        to={val.url}
                        className={`${
                          isActiveLink(val.url)
                            ? "underline underline-offset-8 decoration-4 decoration-[#2F57EF] text-[#000000] font-semibold"
                            : "text-[#626262] no-underline"
                        } hover:text-[#2F57EF]`}
                        onClick={closeDrawer}
                      >
                        {val.text}
                      </Link>
                    ))}
                  </React.Fragment>
                ) : (
                  <Button
                    key={index}
                    variant="header"
                    onClick={() => {
                      link.onClick ? link.onClick() : undefined
                      closeDrawer()
                    }}
                  >
                    {link.text}
                  </Button>
                )
              )}
            </div>
          </div>
        </div>
      </>
    )
  }

  const [openDropdown, setOpenDropdown] = useState(null)

  const handleClick = link => {
    if (link.list) {
      setOpenDropdown(openDropdown === link.text ? null : link.text)
    }
  }

  const renderDesktopSection = () => {
    return (
      <div className="flex items-center md:space-x-3 lg:space-x-6">
        {navigationLinks.map((link, index) => (
          <div key={index} className="relative text-center">
            {link.list ? (
              // Dropdown Button
              <div>
                <button
                  className={`${
                    openDropdown === link.text
                      ? "underline underline-offset-8 decoration-4 decoration-[#2F57EF] text-[#2F57EF] font-semibold"
                      : "text-[#626262] no-underline cursor-pointer"
                  } hover:text-[#2F57EF]`}
                  onClick={() => handleClick(link)}
                >
                  <BodyText variant="bodyText6" className="lg:scale-[1]">
                    {link.text}
                  </BodyText>
                </button>

                {/* Dropdown List */}
                {openDropdown === link.text && (
                  <div className="absolute left-[-50px] mt-4 bg-white drop-shadow-lg rounded-[10px] w-40 z-50 overflow-hidden">
                    {link.list.map((subLink, subIndex) => (
                      <Link
                        key={subIndex}
                        to={subLink.url}
                        className="block px-4 py-3 text-gray-700 hover:bg-gray-200 no-underline text-left border-b-2 "
                      >
                        {subLink.text}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : link.url ? (
              // Normal Navigation Link
              <Link
                to={link.url}
                className={`${
                  isActiveLink(link.url)
                    ? "underline underline-offset-8 decoration-4 decoration-[#2F57EF] text-[#2F57EF] font-semibold"
                    : "text-[#626262] no-underline cursor-pointer"
                } hover:text-[#2F57EF]`}
              >
                <BodyText variant="bodyText6" className="lg:scale-[1]">
                  {link.text}
                </BodyText>
              </Link>
            ) : (
              // Button for actions (like Register)
              <Button
                variant="header"
                onClick={() => {
                  link.onClick && link.onClick()
                }}
              >
                {link.text}
              </Button>
            )}
          </div>
        ))}
      </div>
    )
  }

  const [isSticky, setIsSticky] = useState(false)

  useEffect(() => {
    const navbar = document.getElementsByClassName("temp")[0]
    if (navbar) {
      const sticky = navbar.offsetTop

      const stickyNavbar = () => {
        if (window.scrollY > sticky) {
          setIsSticky(true)
        } else {
          setIsSticky(false)
        }
      }

      window.onscroll = stickyNavbar

      return () => {
        window.onscroll = null
      }
    }
  }, [])

  return (
    <div className="py-0 md:py-4 w-full">
      <div
        className={`temp ${
          isSticky ? styles.sticky : "container"
        } mx-auto md:px-4 md:py-4 ${styles.navbar}`}
      >
        <div
          className={`bg-[#fff] px-4 py-4 flex justify-between items-center gap-2`}
        >
          <Link to={"/"} className="flex flex-1 justify-start items-center">
            <StaticImage
              src="../../images/common/logo/LearnetLogo.png"
              alt="Learnet Logo"
              className={styles.headerLogo}
              layout="constrained"
              placeholder="none"
              formats={["auto", "webp"]}
              outputPixelDensities={[0.5, 0.75, 1, 2]}
            />
          </Link>
          <div className="block md:hidden">{renderMobileSection()}</div>
          <div className="hidden md:block">{renderDesktopSection()}</div>
        </div>
      </div>
      <RegistrationForm
        isOpen={isRegistrationFormOpen}
        setIsOpen={setIsRegistrationFormOpen}
        clickedFrom={"Header"}
      />
    </div>
  )
}

export default Header

const HamburgerIcon = () => {
  return (
    <svg
      className="fill-current h-3 w-3"
      viewBox="0 0 20 20"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 3h20v2H0V3zm0 6h20v2H0V9zm0 6h20v2H0v-2z" />
    </svg>
  )
}
